import ArchiveIcon from '@mui/icons-material/Archive';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestoreIcon from '@mui/icons-material/Restore';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import StatCard from '../../lib/components/StatCard';
import {
  compareVersions,
  getVersionStats,
  mockVersionHistory,
  statusConfig,
  type ComparisonResult,
  type VersionHistoryEntry,
} from './MockData';

const VersionHistory: React.FC = () => {
  const theme = useTheme();
  const [selectedVersions, setSelectedVersions] = useState<string[]>([]);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [comparisonResult, setComparisonResult] = useState<ComparisonResult | null>(null);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [versionToRestore, setVersionToRestore] = useState<VersionHistoryEntry | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const stats = useMemo(() => getVersionStats(), []);

  const filteredVersions = useMemo(() => {
    if (statusFilter === 'all') return mockVersionHistory;
    return mockVersionHistory.filter((v) => v.status === statusFilter);
  }, [statusFilter]);

  const handleVersionSelect = (versionId: string) => {
    setSelectedVersions((prev) => {
      if (prev.includes(versionId)) {
        return prev.filter((id) => id !== versionId);
      }
      if (prev.length >= 2) {
        return [prev[1], versionId];
      }
      return [...prev, versionId];
    });
  };

  const handleCompare = () => {
    if (selectedVersions.length === 2) {
      const result = compareVersions(selectedVersions[0], selectedVersions[1]);
      setComparisonResult(result);
      setCompareDialogOpen(true);
    }
  };

  const handleRestore = (version: VersionHistoryEntry) => {
    setVersionToRestore(version);
    setRestoreDialogOpen(true);
  };

  const confirmRestore = () => {
    // In real app, this would call the store
    setRestoreDialogOpen(false);
    setVersionToRestore(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getChangeIndicator = (delta: number | undefined, inverted = false) => {
    if (delta === undefined || delta === 0) return null;
    const isPositive = inverted ? delta < 0 : delta > 0;
    return (
      <Chip
        size="small"
        label={`${delta > 0 ? '+' : ''}${delta}`}
        color={isPositive ? 'success' : 'error'}
        sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
      />
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Version History
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View, compare, and restore previous timetable versions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<CompareArrowsIcon />}
            disabled={selectedVersions.length !== 2}
            onClick={handleCompare}
          >
            Compare Selected ({selectedVersions.length}/2)
          </Button>
          <IconButton>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            stat={{
              label: 'Total Versions',
              value: stats.totalVersions,
              icon: <HistoryIcon />,
              color: theme.palette.primary.main,
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            stat={{
              label: 'Current Version',
              value: `v${stats.currentVersion}`,
              icon: <CheckCircleIcon />,
              color: theme.palette.success.main,
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            stat={{
              label: 'Finalized',
              value: stats.finalizedCount,
              icon: <CheckCircleIcon />,
              color: theme.palette.info.main,
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            stat={{
              label: 'Archived',
              value: stats.archivedCount,
              icon: <ArchiveIcon />,
              color: theme.palette.warning.main,
            }}
          />
        </Grid>
      </Grid>

      {/* Filter */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status Filter</InputLabel>
          <Select
            value={statusFilter}
            label="Status Filter"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All Versions</MenuItem>
            <MenuItem value="generated">Generated</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="finalized">Finalized</MenuItem>
            <MenuItem value="archived">Archived</MenuItem>
          </Select>
        </FormControl>
        {selectedVersions.length > 0 && (
          <Button size="small" onClick={() => setSelectedVersions([])}>
            Clear Selection
          </Button>
        )}
      </Box>

      {/* Version Timeline */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Version Timeline
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Version</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>By</TableCell>
                  <TableCell align="center">Lessons</TableCell>
                  <TableCell align="center">Conflicts</TableCell>
                  <TableCell align="center">Coverage</TableCell>
                  <TableCell>Changes</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredVersions.map((version) => {
                  const isSelected = selectedVersions.includes(version.id);
                  return (
                    <TableRow
                      key={version.id}
                      hover
                      selected={isSelected}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: isSelected ? 'action.selected' : undefined,
                      }}
                      onClick={() => handleVersionSelect(version.id)}
                    >
                      <TableCell padding="checkbox">
                        <Chip
                          size="small"
                          label={isSelected ? '✓' : '○'}
                          color={isSelected ? 'primary' : 'default'}
                          sx={{ minWidth: 32 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="bold">v{version.version}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{version.name}</Typography>
                          {version.notes && (
                            <Typography variant="caption" color="text.secondary">
                              {version.notes.substring(0, 50)}
                              {version.notes.length > 50 ? '...' : ''}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={statusConfig[version.status]?.label || version.status}
                          color={statusConfig[version.status]?.color || 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDate(version.createdAt)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{version.createdByName}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {version.stats.totalLessons}
                          {getChangeIndicator(version.changesFromPrevious?.lessonsAdded)}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {version.stats.conflictCount}
                          {getChangeIndicator(version.changesFromPrevious?.conflictDelta, true)}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          color={version.stats.curriculumCoverage >= 98 ? 'success.main' : 'text.secondary'}
                        >
                          {version.stats.curriculumCoverage}%
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {version.changesFromPrevious && (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {version.changesFromPrevious.lessonsModified > 0 && (
                              <Chip
                                size="small"
                                label={`${version.changesFromPrevious.lessonsModified} modified`}
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.65rem' }}
                              />
                            )}
                          </Box>
                        )}
                        {version.tags.map((tag) => (
                          <Chip
                            key={tag}
                            size="small"
                            label={tag}
                            color={tag === 'current' ? 'primary' : tag === 'finalized' ? 'success' : 'default'}
                            sx={{ ml: 0.5, height: 20, fontSize: '0.65rem' }}
                          />
                        ))}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          {version.canRestore && (
                            <Tooltip title="Restore this version">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRestore(version);
                                }}
                              >
                                <RestoreIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Compare Dialog */}
      <Dialog open={compareDialogOpen} onClose={() => setCompareDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CompareArrowsIcon />
            Version Comparison
          </Box>
        </DialogTitle>
        <DialogContent>
          {comparisonResult && (
            <Box>
              {/* Version Headers */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 6 }}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, bgcolor: 'action.hover', borderColor: 'primary.main' }}
                  >
                    <Typography variant="subtitle2" color="primary">
                      Version A
                    </Typography>
                    <Typography variant="h6">{comparisonResult.versionA.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      v{comparisonResult.versionA.version} •{' '}
                      {formatDate(comparisonResult.versionA.createdAt)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, bgcolor: 'action.hover', borderColor: 'secondary.main' }}
                  >
                    <Typography variant="subtitle2" color="secondary">
                      Version B
                    </Typography>
                    <Typography variant="h6">{comparisonResult.versionB.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      v{comparisonResult.versionB.version} •{' '}
                      {formatDate(comparisonResult.versionB.createdAt)}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Summary */}
              <Alert
                severity={
                  comparisonResult.summary.overallBetter === 'B'
                    ? 'success'
                    : comparisonResult.summary.overallBetter === 'A'
                      ? 'warning'
                      : 'info'
                }
                sx={{ mb: 3 }}
              >
                {comparisonResult.summary.recommendation}
              </Alert>

              {/* Comparison Table */}
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Category</TableCell>
                      <TableCell>Metric</TableCell>
                      <TableCell align="center">Version A</TableCell>
                      <TableCell align="center">Version B</TableCell>
                      <TableCell align="center">Change</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {comparisonResult.differences.map((diff, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{diff.category}</TableCell>
                        <TableCell>{diff.metricName}</TableCell>
                        <TableCell align="center">{diff.valueA}</TableCell>
                        <TableCell align="center">{diff.valueB}</TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={diff.change}
                            color={
                              diff.change === 'improved'
                                ? 'success'
                                : diff.change === 'worsened'
                                  ? 'error'
                                  : 'default'
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompareDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Restore Dialog */}
      <Dialog open={restoreDialogOpen} onClose={() => setRestoreDialogOpen(false)}>
        <DialogTitle>Restore Version</DialogTitle>
        <DialogContent>
          {versionToRestore && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This will create a new version based on {versionToRestore.name}. The current version
                will be preserved in history.
              </Alert>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2">
                <strong>Version:</strong> v{versionToRestore.version}
              </Typography>
              <Typography variant="body2">
                <strong>Name:</strong> {versionToRestore.name}
              </Typography>
              <Typography variant="body2">
                <strong>Created:</strong> {formatDate(versionToRestore.createdAt)}
              </Typography>
              <Typography variant="body2">
                <strong>Lessons:</strong> {versionToRestore.stats.totalLessons}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={confirmRestore}>
            Restore Version
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VersionHistory;

